import { Trans, useLingui } from '@lingui/react/macro';
import { parseISO } from 'date-fns';

import Box from '@/design_system/Box';
import Stack from '@/design_system/Stack';
import InlineText from '@/design_system/Typography/InlineText';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconValidation from '@/icons/Validation.svg';
import { useAuthenticityCertificate } from '@/models/uniqueArticle';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import { formatDate } from '@/utils/date';
import useViewPort from '@/utils/useViewport';

import { Item } from './Item';

export const ArticleAuthenticityCertificate = ({ mode }: { mode: 'form' | 'sidebar' }) => {
  const { t } = useLingui();
  const { isMobile } = useViewPort();
  const { article, view } = useArticleContext();

  const { data: certificate } = useAuthenticityCertificate(article.uniqueArticle?.id);

  if (!certificate) {
    return null;
  }

  const certificateId = certificate.externalId;
  const activationLocation = certificate.store?.name;
  const activationDate = certificate.activationDate
    ? formatDate(parseISO(certificate.activationDate), { dateStyle: 'medium' })
    : undefined;

  const showActivationLocation =
    !!activationLocation && view.warranty.fields.authenticityCertificate.activationLocation.shown;
  const showActivationDate =
    !!activationDate && view.warranty.fields.authenticityCertificate.activationDate.shown;

  if (mode === 'form') {
    return (
      <Box padding="8px 12px">
        <Stack row alignItems="flex-start" gap="0.5rem" flexWrap="nowrap">
          <IconValidation className="color-primary-700" style={{ fontSize: '1.5rem' }} />
          <Paragraph color="secondary">
            <Trans id="article.information.certificate">
              Active certificate: <InlineText weight="medium">{certificateId}</InlineText>
            </Trans>
            {(showActivationLocation || showActivationDate) && (
              <>
                {isMobile ? <br /> : ' • '}
                {showActivationLocation && showActivationDate ? (
                  <Trans id="article.information.certificate.location-date">
                    Activated at <InlineText weight="medium">{activationLocation}</InlineText> on{' '}
                    <InlineText weight="medium">{activationDate}</InlineText>
                  </Trans>
                ) : showActivationLocation ? (
                  <Trans id="article.information.certificate.location">
                    Activated at <InlineText weight="medium">{activationLocation}</InlineText>
                  </Trans>
                ) : (
                  <Trans id="article.information.certificate.date">
                    Activated on <InlineText weight="medium">{activationDate}</InlineText>
                  </Trans>
                )}
              </>
            )}
          </Paragraph>
        </Stack>
      </Box>
    );
  }

  if (mode === 'sidebar') {
    return (
      <Item
        label={t({ id: 'article.information.certificate.label', message: 'Active certificate' })}
      >
        <Paragraph weight="medium">{certificateId}</Paragraph>
        {(showActivationLocation || showActivationDate) && (
          <Paragraph size="200">
            {activationLocation}
            {showActivationLocation && showActivationDate && ' • '}
            {activationDate}
          </Paragraph>
        )}
      </Item>
    );
  }

  return null;
};
